/**
 * @fileoverview User-related Redux selectors.
 *
 * Contains safe selectors for accessing user data from the Redux state tree,
 * handling missing/partial state during initialization or hydration.
 */

import defaultLoginSettings from 'store/data/user_templates/default_user.json';

/**
 * Select the current user's limits from the Redux state tree.
 *
 * Safe against missing/partial state (e.g., during app init or hydration).
 *
 * @param {object} state - The Redux root state.
 * @returns {object|null} The user's limits object, or null if unavailable.
 */
export const selectUserLimits = (state) => {
    if (!state || typeof state !== 'object') return null

    const limits = state?.inputData?.present?.loginData?.limits
    return limits && typeof limits === 'object' ? limits : null
}


/**
 * Select the current user's ID from the Redux state tree.
 *
 * Safe against missing/partial state (e.g., during app init or hydration).
 *
 * @param {object} state - The Redux root state.
 * @returns {string|number|null} The user's ID, or null if unavailable/invalid.
 */
export const selectUserId = (state) => {
    if (!state || typeof state !== 'object') return null

    const userId = state?.inputData?.present?.loginData?.userId

    if (typeof userId === 'string') return userId.trim() ? userId : null
    if (typeof userId === 'number') return Number.isFinite(userId) ? userId : null

    return null
}

/**
 * Select the current user's plan name from the Redux state tree.
 *
 * Safe against missing/partial state (e.g., during app init or hydration).
 *
 * @param {object} state - The Redux root state.
 * @returns {string|null} The user's plan name, or null if unavailable/invalid.
 */
export const selectPlan = (state) => {
    if (!state || typeof state !== 'object') return null

    const plan = state?.inputData?.present?.loginData?.plan
    if (typeof plan !== 'string') return null
    const trimmed = plan.trim()
    return trimmed || null
}

/**
 * Create a selector that returns the maximum number of rows the current user can add for a table.
 *
 * This is typically based on the user's subscription limits stored in `loginData.limits`.
 *
 * Safe against missing/partial state.
 *
 * @param {string} tableName - The table name key used in limits (e.g., "buildings").
 * @returns {(state: object) => number|null} A selector that returns max rows or null if unavailable.
 */
export const selectUserRowLimit = (tableName) => (state) => {
    if (!state || typeof state !== 'object') return null
    if (typeof tableName !== 'string' || !tableName.trim()) return null

    const maxRows = state?.inputData?.present?.loginData?.limits?.[tableName]?.maxRows
    return Number.isFinite(maxRows) ? maxRows : null
}

/**
 * Create a selector that returns the number of remaining rows the current user can add for a table.
 *
 * Computed as: `maxRows - currentRowCount`.
 * - Prefers the logged-in user's limit from `loginData.limits`.
 * - Falls back to `defaultLoginSettings.limits` if available (when not logged in / missing limits).
 *
 * Safe against missing/partial state.
 *
 * @param {string} tableName - The table name key used in limits and data (e.g., "buildings").
 * @returns {(state: object) => number|null} A selector that returns remaining rows or null if unavailable.
 */
export const selectUserRemainingRows = (tableName) => state => {
  //checks to see how many rows this user can add to the table
  const curState = state.inputData.present;
  if (curState.loginData && curState.loginData.limits[tableName] && curState.loginData.limits[tableName]['maxRows']) {
    const remaining = curState.loginData.limits[tableName]['maxRows'] - curState.data[tableName].length
    return remaining
  } else {
    return defaultLoginSettings['limits'][tableName]['maxRows'] - curState.data[tableName].length
  }
}
